import {
  FontSizes,
  FontWeights,
  IBreadcrumbStyles,
  Breadcrumb as OfficeBreadcrumb,
  mergeStyleSets,
} from "@fluentui/react";
import { ITheme, useComponentLogger } from "cayo.ui";
import React, { FC, useContext, useMemo } from "react";
import styled, { ThemeContext } from "styled-components";
import { IElementStyles } from "../api/schema.api";
import { useBreadcrumbs } from "./Nav/nav-hooks";

type BreadcrumbsProps = {
  rootName?: string;
};

const Breadcrumb: FC<BreadcrumbsProps> = ({ rootName }) => {
  const items = useBreadcrumbs(rootName);
  const theme = useContext<ITheme>(ThemeContext);
  const log = useComponentLogger(Breadcrumb);

  const styles = useMemo(() => mergeStyleSets(bcStyle(theme)), [theme]);

  log.debug("Nav.breadcrumbs -> items", items);

  // seems like the problem is in the FluentUI component itself
  // so we change the react key to enforce rerender
  const rerenderKey = items.map((breadcrumb) => breadcrumb.key).join("/");

  return (
    <BreadcrumbContainer>
      <OfficeBreadcrumb key={rerenderKey} items={items} styles={styles} />
    </BreadcrumbContainer>
  );
};

const BreadcrumbContainer = styled.div`
  flex-grow: 1;
  min-width: 30px;
`;

const bcStyle = (theme: ITheme): Partial<IBreadcrumbStyles> => {
  const colors = theme.cayoTheme.brandColors;
  const { semanticColors } = theme.officeTheme;
  const navHeaderStyles = (theme.elementStyles as unknown as IElementStyles).navHeader;
  const itemTextColor = navHeaderStyles?.["text-color"] || semanticColors.primaryButtonText;

  //const hoverText = navHeaderStyles?.["selected-item-text-color"] || semanticColors.buttonTextHovered;
  return {
    chevron: {
      color: itemTextColor,
      fontSize: "16px",
    },
    item: {
      color: colors.divider,
      fontSize: "18px",
      fontWeight: FontWeights.regular,
    },
    listItem: {
      selectors: {
        ":last-child .ms-Breadcrumb-item": {
          color: itemTextColor,
          fontWeight: FontWeights.regular,
        },
      },
    },
    itemLink: {
      background: "transparent",
      color: itemTextColor,
      fontSize: "18px",
      fontWeight: FontWeights.regular,
      outline: "none",
      selectors: {
        ":focus": {
          background: "transparent",
          color: `${itemTextColor} !important`,
          textDecoration: "underline",
        },

        ":hover": {
          background: "transparent",
          color: `${itemTextColor} !important`,
          textDecoration: "underline",
        },

        ":last-child": {
          color: `${itemTextColor} !important`,
        },
        ":hover:focus": {
          background: "transparent",
          color: `${itemTextColor} !important`,
          textDecoration: "underline",
        },
      },
    },
    overflow: {
      fontSize: FontSizes.medium,
      fontWeight: FontWeights.regular,
    },
    overflowButton: {
      color: `${itemTextColor} !important`,
      fontSize: FontSizes.medium,
      fontWeight: FontWeights.regular,
      selectors: {
        "&:hover": {
          backgroundColor: semanticColors.buttonBackgroundHovered,
          color: semanticColors.buttonTextHovered,
        },
        ":focus": {
          backgroundColor: semanticColors.buttonBackgroundPressed,
          color: semanticColors.buttonTextPressed,
        },
        ":hover:focus": {
          backgroundColor: semanticColors.buttonBackgroundPressed,
          color: semanticColors.buttonTextPressed,
        },
      },
    },
    root: {
      fontSize: FontSizes.medium,
      fontWeight: FontWeights.regular,
      margin: "auto 0",
    },
  };
};

export default Breadcrumb;
